import {
  NavState,
  NavbarInit,
  AccountSwitcherInit,
} from '@amzn/aac-chrome-internal-api';
import { logger } from 'src/commons/utils/logger';
import { diffGlobalNav, diffAccountInfo } from './diffNavState';
import { getBtasNavStatePromise, getShadowId } from './shadowContext';
import { emitGlobalNavShadow, emitAccountInfoShadow } from './shadowMetrics';

const logMismatch = (
  service: 'GlobalNav' | 'AccountInfo',
  reason: string,
): void => {
  // logger.info (not warn) — mismatches are the expected observation signal
  // during shadow rollout, not an error. logger.info in this package writes to
  // console only in dev and to Katal in prod; volume is bounded by session
  // count, not by mismatch count within a session (each diff runs at most once
  // per page load).
  logger.info(
    `Shadow.${service}.Mismatch shadowId=${getShadowId() ?? '<none>'} reason=${reason}`,
  );
};

export const runGlobalNavShadow = async (
  trimResult: NavbarInit | { error: unknown },
): Promise<void> => {
  const btasPromise = getBtasNavStatePromise();
  if (!btasPromise) {
    // No BTAS side to compare against — Nav.tsx never stashed the promise
    // (Trim-hosted page, spoofer mode, or an isolated render tree). Silent
    // return: this is an expected absence, not an error. Emitting a metric
    // here would double-count with real BTAS failures during ops triage.
    return;
  }

  let btas: NavState | null = null;
  try {
    btas = await btasPromise;
  } catch {
    emitGlobalNavShadow('BtasError');
    return;
  }

  if ('error' in trimResult) {
    emitGlobalNavShadow('NavbarInitError');
    return;
  }

  try {
    const diff = diffGlobalNav(btas.globalNav, trimResult.globalNav);
    emitGlobalNavShadow(diff.match ? 'Match' : 'Mismatch');
    if (!diff.match) {
      logMismatch('GlobalNav', diff.reasons[0] || '<none>');
    }
  } catch (e) {
    logger.error(
      'runGlobalNavShadow diff threw',
      e instanceof Error ? e : new Error(String(e)),
    );
    emitGlobalNavShadow('DiffThrew');
  }
};

export const runAccountInfoShadow = async (
  trimResult: AccountSwitcherInit | { error: unknown },
): Promise<void> => {
  const btasPromise = getBtasNavStatePromise();
  if (!btasPromise) {
    // See runGlobalNavShadow — silent return on missing BTAS promise so
    // Trim-hosted / spoofer sessions don't inflate error counters.
    return;
  }

  let btas: NavState | null = null;
  try {
    btas = await btasPromise;
  } catch {
    emitAccountInfoShadow('BtasError');
    return;
  }

  if ('error' in trimResult) {
    emitAccountInfoShadow('AccountSwitcherInitError');
    return;
  }

  try {
    const diff = diffAccountInfo(btas.accountInfo, trimResult);
    emitAccountInfoShadow(diff.match ? 'Match' : 'Mismatch');
    if (!diff.match) {
      logMismatch('AccountInfo', diff.reasons[0] || '<none>');
    }
  } catch (e) {
    logger.error(
      'runAccountInfoShadow diff threw',
      e instanceof Error ? e : new Error(String(e)),
    );
    emitAccountInfoShadow('DiffThrew');
  }
};
